import React, { type ReactNode } from 'react'
import classNames from 'classnames'

import { Button, type ButtonVariant } from '../../../../components/Button/Button'
import { LinkButton } from '../../../../components/Button/LinkButton'

import styles from './FeaturePromoEmptyState.module.scss'

export type FeaturePromoAction = { label: string } & ({ onClick: () => void } | { to: string })

export interface FeaturePromoEmptyStateProps {
  title: string
  description: string
  primaryAction?: FeaturePromoAction
  secondaryAction?: FeaturePromoAction
  footnote?: string
  helpLink?: { label: string; href: string }
  illustration?: ReactNode
}

const ACTION_CLASS_NAME = 'button-medium'

const PromoAction: React.FC<{ action: FeaturePromoAction; variant: ButtonVariant }> = ({ action, variant }) =>
  'to' in action ? (
    <LinkButton variant={variant} className={classNames(ACTION_CLASS_NAME, 'text-center text-decoration-none')} to={action.to}>
      {action.label}
    </LinkButton>
  ) : (
    <Button variant={variant} className={ACTION_CLASS_NAME} onClick={action.onClick}>
      {action.label}
    </Button>
  )

export const FeaturePromoEmptyState: React.FC<FeaturePromoEmptyStateProps> = ({
  title,
  description,
  primaryAction,
  secondaryAction,
  footnote,
  helpLink,
  illustration,
}) => (
  <div className={classNames(styles.card, 'mx-auto w-100 d-flex flex-column align-items-center text-center gap-3')}>
    {illustration}
    <div className='d-flex flex-column gap-2'>
      <h4 className='mb-0'>{title}</h4>
      <div className='paragraph-medium zocks-text-gray-600'>{description}</div>
    </div>
    {primaryAction || secondaryAction ? (
      <div className='d-flex justify-content-center gap-2 w-100'>
        {primaryAction ? <PromoAction action={primaryAction} variant='primary' /> : null}
        {secondaryAction ? <PromoAction action={secondaryAction} variant='header' /> : null}
      </div>
    ) : null}
    {footnote || helpLink ? (
      <div className='d-flex flex-column gap-2'>
        {footnote ? <div className='paragraph-small zocks-text-gray-600'>{footnote}</div> : null}
        {helpLink ? (
          <a
            className='paragraph-small bold zocks-text-primary text-decoration-none d-inline-flex align-items-center justify-content-center gap-1'
            href={helpLink.href}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={`${helpLink.label} (opens in a new tab)`}
          >
            {helpLink.label}
            <i className='bi bi-box-arrow-up-right' aria-hidden='true' />
          </a>
        ) : null}
      </div>
    ) : null}
  </div>
)
